/**
 * CRITICAL AUTHENTICATION MODULE - DO NOT MODIFY WITHOUT EXTREME CAUTION
 *
 * This file handles caching of user authentication data for performance
 * and offline capability.
 *
 * PROTECTION: This file is protected by pre-commit hooks and requires
 * EXTRA_APPROVAL=1 environment variable to modify.
 *
 * Last tested: Working in Chrome and Firefox
 * Last modified: [DATE] - [REASON]
 */

import { AppUser } from "@/lib/types/database";
import { logger } from "@/lib/logger";

/**
 * Cache key prefix for app user data
 */
const CACHE_KEY_PREFIX = "cached_app_user_";

/**
 * Get cached user data from localStorage
 *
 * @param userId - User ID to retrieve from cache
 * @returns Cached user data or null if not found
 */
export function getCachedUser(userId: string): AppUser | null {
  try {
    const cachedData = localStorage.getItem(`${CACHE_KEY_PREFIX}${userId}`);
    if (cachedData) {
      logger.info("🔍 Using cached user data for userId", { userId });
      return JSON.parse(cachedData);
    }
  } catch (error) {
    logger.warn("🔍 Failed to retrieve cached user data", { error });
  }
  return null;
}

/**
 * Cache user data in localStorage
 *
 * @param userId - User ID to cache
 * @param userData - User data to cache
 */
export function setCachedUser(userId: string, userData: AppUser): void {
  try {
    const cacheKey = `${CACHE_KEY_PREFIX}${userId}`;
    localStorage.setItem(cacheKey, JSON.stringify(userData));
    logger.info("🔍 User data cached for userId", { userId });
  } catch (error) {
    logger.warn("🔍 Failed to cache user data", { error });
  }
}

/**
 * Remove cached user data
 *
 * @param userId - User ID to remove from cache
 */
export function removeCachedUser(userId: string): void {
  try {
    const cacheKey = `${CACHE_KEY_PREFIX}${userId}`;
    localStorage.removeItem(cacheKey);
    logger.info("🔍 Cached user data removed for userId", { userId });
  } catch (error) {
    logger.warn("🔍 Failed to remove cached user data", { error });
  }
}

/**
 * Clear all cached user data
 */
export function clearAllCachedUsers(): void {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      if (key.startsWith(CACHE_KEY_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
    logger.info("🔍 All cached user data cleared");
  } catch (error) {
    logger.warn("🔍 Failed to clear cached user data", { error });
  }
}
